import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs';
import { TwoFactor } from '../../../../services/twofa';
import { TotpSetupResponse, TwoFactorMethod, TwoFactorStatus } from '../../../../types/twofa';

@Component({
  selector: 'app-twofa-setup',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSelectModule,
  ],
  templateUrl: './twofa-setup.html',
  styleUrl: './twofa-setup.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorSetup implements OnInit {
  private service = inject(TwoFactor);
  private toastr = inject(ToastrService);
  private dialogRef = inject(MatDialogRef<TwoFactorSetup>);

  loading = signal(true);
  busy = signal(false);
  status = signal<TwoFactorStatus | null>(null);

  totpSetup = signal<TotpSetupResponse | null>(null);
  recoveryCodes = signal<string[] | null>(null);
  emailEnrollSent = signal(false);

  totpCode = new FormControl('', { nonNullable: true });
  emailCode = new FormControl('', { nonNullable: true });
  disableTotpCode = new FormControl('', { nonNullable: true });
  disableEmailCode = new FormControl('', { nonNullable: true });
  verifyMethod = new FormControl<TwoFactorMethod>('TOTP', { nonNullable: true });
  verifyCode = new FormControl('', { nonNullable: true });

  totpEnabled = computed(() => this.status()?.activeMethods.includes('TOTP') ?? false);
  emailEnabled = computed(() => this.status()?.activeMethods.includes('EMAIL') ?? false);
  anyEnabled = computed(() => (this.status()?.activeMethods.length ?? 0) > 0);
  qrDataUri = computed(() => {
    const s = this.totpSetup();
    return s ? `data:image/png;base64,${s.qrPngBase64}` : null;
  });

  ngOnInit(): void {
    this.loadStatus();
  }

  private loadStatus(): void {
    this.loading.set(true);
    this.service
      .getStatus()
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (s) => {
          this.status.set(s);
          this.seedVerifyMethod(s);
        },
        error: () => this.toastr.error('Nem sikerült betölteni a 2FA állapotot.', 'Hiba'),
      });
  }

  /**
   * Keeps the verify-tester's method select on an option that is actually
   * rendered: the TOTP/EMAIL options only exist for active methods, so a
   * stale value (e.g. the initial 'TOTP' with email-only 2FA) would leave the
   * select blank and every verify attempt failing.
   */
  private seedVerifyMethod(s: TwoFactorStatus): void {
    const current = this.verifyMethod.value;
    if (current === 'RECOVERY' || s.activeMethods.includes(current)) return;
    const preferred = s.activeMethods.includes(s.preferredMethod) ? s.preferredMethod : s.activeMethods[0];
    this.verifyMethod.setValue((preferred as TwoFactorMethod) ?? 'RECOVERY');
  }

  // ---- TOTP ----
  beginTotp(): void {
    this.busy.set(true);
    this.recoveryCodes.set(null);
    this.service
      .setupTotp()
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (res) => this.totpSetup.set(res),
        error: () => this.toastr.error('Nem sikerült elindítani a TOTP beállítást.', 'Hiba'),
      });
  }

  activateTotp(): void {
    const code = this.totpCode.value.trim();
    if (!code) return;
    this.busy.set(true);
    this.service
      .activateTotp(code)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (res) => {
          this.recoveryCodes.set(res.recoveryCodes);
          this.totpSetup.set(null);
          this.totpCode.reset();
          this.toastr.success('TOTP bekapcsolva.', 'Kész');
          this.loadStatus();
        },
        error: () => this.toastr.error('Érvénytelen kód.', 'Hiba'),
      });
  }

  // ---- Email OTP ----
  sendEmailEnroll(): void {
    this.busy.set(true);
    this.service
      .setupEmail()
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.emailEnrollSent.set(true);
          this.toastr.info('Aktiváló kód elküldve (fejlesztői módban a szerver naplójában látható).', 'Kód elküldve');
        },
        error: () => this.toastr.error('Nem sikerült elküldeni a kódot.', 'Hiba'),
      });
  }

  activateEmail(): void {
    const code = this.emailCode.value.trim();
    if (!code) return;
    this.busy.set(true);
    this.service
      .activateEmail(code)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          this.emailEnrollSent.set(false);
          this.emailCode.reset();
          this.toastr.success('E-mail alapú 2FA bekapcsolva.', 'Kész');
          this.loadStatus();
        },
        error: () => this.toastr.error('Érvénytelen kód.', 'Hiba'),
      });
  }

  // ---- Verify tester ----
  sendEmailLoginCode(): void {
    this.busy.set(true);
    this.service
      .sendEmailLoginCode()
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => this.toastr.info('Belépési kód elküldve (a szerver naplójában látható).', 'Kód elküldve'),
        error: () => this.toastr.error('Nem sikerült elküldeni a kódot.', 'Hiba'),
      });
  }

  verify(): void {
    const code = this.verifyCode.value.trim();
    if (!code) return;
    this.busy.set(true);
    this.service
      .verify(this.verifyMethod.value, code)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: (res) => {
          if (res.valid) {
            this.toastr.success('A kód érvényes! ✔', 'Sikeres ellenőrzés');
          } else {
            this.toastr.warning('A kód érvénytelen.', 'Sikertelen');
          }
        },
        error: () => this.toastr.error('A kód érvénytelen.', 'Sikertelen'),
      });
  }

  // ---- Disable ----
  disable(method: TwoFactorMethod): void {
    const control = method === 'TOTP' ? this.disableTotpCode : this.disableEmailCode;
    const code = control.value.trim();
    if (!code) {
      this.toastr.warning('Adjon meg egy érvényes kódot a kikapcsoláshoz.', 'Kód szükséges');
      return;
    }
    this.busy.set(true);
    this.service
      .disable(method, code)
      .pipe(finalize(() => this.busy.set(false)))
      .subscribe({
        next: () => {
          control.reset();
          this.toastr.success('A módszer kikapcsolva.', 'Kész');
          this.loadStatus();
        },
        error: () => this.toastr.error('Érvénytelen kód.', 'Hiba'),
      });
  }

  copyRecoveryCodes(): void {
    const codes = this.recoveryCodes();
    if (!codes) return;
    // navigator.clipboard is undefined in insecure (plain-HTTP) contexts, and
    // writeText can reject on denied permission — only report success when the
    // codes actually reached the clipboard, since they cannot be shown again.
    if (!navigator.clipboard) {
      this.toastr.error('A vágólap nem érhető el — másolja ki a kódokat kézzel.', 'Hiba');
      return;
    }
    navigator.clipboard.writeText(codes.join('\n')).then(
      () => this.toastr.success('Helyreállító kódok a vágólapra másolva.', 'Másolva'),
      () => this.toastr.error('Nem sikerült a vágólapra másolni — másolja ki a kódokat kézzel.', 'Hiba'),
    );
  }

  close(): void {
    this.dialogRef.close();
  }
}
